import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import {
    Card,
    CardBody,
    CardHeader,
    Button,
    DatePicker,
    NumberInput,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Select,
    SelectItem,
    Textarea,
    Spinner,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Divider,
} from "@heroui/react";
import { LuArrowLeft, LuSave, LuPlus, LuTrash } from "react-icons/lu";
import axios from "axios";
import { useFetchData, usePostData } from "@/hooks";
import { findOptionTitle, showToast, formatDate } from "@/utils";
import { ThemedText, PageHeader, ErrorState } from "@/components/ui";
import { getLocalTimeZone, today, parseDate } from "@internationalized/date";
import { BASE_API } from "@/constants";

// Helper component to display stock info
const StockInfo = ({
    control,
    index,
    setValue,
    trigger,
    units = [],
    onLoadingChange,
}) => {
    const warehouseId = useWatch({
        control,
        name: `items.${index}.warehouse_id`,
    });
    const materialId = useWatch({
        control,
        name: `items.${index}.material_id`,
    });
    const unitId = useWatch({
        control,
        name: `items.${index}.material_unit_id`,
    });

    const { data: stockData, isLoading } = useFetchData(
        warehouseId && materialId && unitId
            ? "stocks/check-material-balance"
            : null,
        {
            warehouse_id: warehouseId,
            material_id: materialId,
            material_unit_id: unitId,
        },
        true,
    );

    useEffect(() => {
        if (stockData?.balance !== undefined) {
            setValue(`items.${index}.max_stock`, stockData.balance);
            if (trigger) trigger(`items.${index}.quantity`);
        }
    }, [stockData, setValue, index, trigger]);

    // Notify parent about loading status
    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(index, isLoading);
        }
    }, [isLoading, index, onLoadingChange]);

    if (!warehouseId || !materialId || !unitId) {
        return (
            <ThemedText size="sm" variant="secondary" className="mt-1.5">
                -
            </ThemedText>
        );
    }

    if (isLoading) {
        return (
            <ThemedText size="xs" variant="secondary">
                Memeriksa stok..
            </ThemedText>
        );
    }

    const balance = stockData?.balance ?? 0;
    const isLow = balance <= 0;
    const unitName = findOptionTitle(units, unitId) || "";

    return (
        <ThemedText
            size="sm"
            className={`mt-1.5 ${isLow ? "text-danger" : "text-success"}`}
        >
            {parseFloat(balance)} {unitName}
        </ThemedText>
    );
};

const ProductionCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    // State untuk menyimpan satuan dan loading state
    const [unitsState, setUnitsState] = useState({
        parent: [], // satuan untuk produk hasil
        parentLoading: false, // loading state parent
        items: {}, // satuan per index item: { 0: [], 1: [], ... }
        itemsLoading: {}, // loading per index: { 0: false, 1: true, ... }
    });

    const [stockLoadingStatus, setStockLoadingStatus] = useState({});

    const handleStockLoading = useCallback((index, isLoading) => {
        setStockLoadingStatus((prev) => {
            if (prev[index] === isLoading) return prev;
            return { ...prev, [index]: isLoading };
        });
    }, []);

    const isStockChecking = Object.values(stockLoadingStatus).some(Boolean);

    // Fetch Master Data
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");
    const { data: allMaterials, isLoading: isLoadingMaterials } =
        useFetchData("select/material");

    // Fetch Initial Data for Edit
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(isEdit ? `material-productions/${id}/edit` : null);

    // Form Setup
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        reset,
        getValues,
        trigger,
        formState: { errors, isValid },
    } = useForm({
        defaultValues: {
            production_date: today(getLocalTimeZone()),
            description: "",
            warehouse_id: "",
            material_id: "",
            material_unit_id: "",
            quantity: null,
            items: [
                {
                    material_id: "",
                    warehouse_id: "",
                    quantity: null,
                    material_unit_id: "",
                },
            ],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "items",
    });

    const watchMaterialId = watch("material_id");
    const watchedItems = watch("items");

    // Helper to filter available materials for a specific row
    const getAvailableMaterials = (currentIndex) => {
        if (!allMaterials) return [];
        const selectedMaterialIds = watchedItems.map(
            (item) => item.material_id,
        );
        return allMaterials.filter((material) => {
            const isSelectedInOtherRow =
                selectedMaterialIds.includes(material.value) &&
                watchedItems[currentIndex]?.material_id !== material.value;
            return !isSelectedInOtherRow;
        });
    };

    // Fetch satuan parent ketika material_id berubah
    useEffect(() => {
        const fetchParentUnits = async () => {
            if (!watchMaterialId) {
                setUnitsState((prev) => ({
                    ...prev,
                    parent: [],
                    parentLoading: false,
                }));
                return;
            }
            // Set loading
            setUnitsState((prev) => ({ ...prev, parentLoading: true }));
            try {
                const response = await axios.get(
                    `${BASE_API}/select/material-unit`,
                    { params: { material_id: watchMaterialId } },
                );
                setUnitsState((prev) => ({
                    ...prev,
                    parent: response.data.data || [],
                    parentLoading: false,
                }));
            } catch (error) {
                console.error("Error fetching parent units:", error);
                setUnitsState((prev) => ({ ...prev, parentLoading: false }));
            }
        };
        fetchParentUnits();
    }, [watchMaterialId]);

    // Fungsi untuk fetch satuan item
    const fetchItemUnits = async (index, materialId) => {
        if (!materialId) {
            setUnitsState((prev) => ({
                ...prev,
                items: { ...prev.items, [index]: [] },
                itemsLoading: { ...prev.itemsLoading, [index]: false },
            }));
            return;
        }
        // Set loading
        setUnitsState((prev) => ({
            ...prev,
            itemsLoading: { ...prev.itemsLoading, [index]: true },
        }));
        try {
            const response = await axios.get(
                `${BASE_API}/select/material-unit`,
                { params: { material_id: materialId } },
            );
            setUnitsState((prev) => ({
                ...prev,
                items: { ...prev.items, [index]: response.data.data || [] },
                itemsLoading: { ...prev.itemsLoading, [index]: false },
            }));
        } catch (error) {
            console.error("Error fetching item units:", error);
            setUnitsState((prev) => ({
                ...prev,
                itemsLoading: { ...prev.itemsLoading, [index]: false },
            }));
        }
    };

    // Populate form for Edit
    useEffect(() => {
        if (isEdit && initialData) {
            reset({
                production_date: parseDate(initialData.production_date),
                description: initialData.description || "",
                warehouse_id: String(initialData.warehouse_id),
                material_id: String(initialData.material_id),
                material_unit_id: String(initialData.material_unit_id),
                quantity: Number(initialData.quantity),
                items: initialData.items.map((item) => ({
                    material_id: String(item.material_id),
                    warehouse_id: String(item.warehouse_id),
                    quantity: Number(item.quantity),
                    material_unit_id: String(item.material_unit_id),
                })),
            });

            // Fetch item units
            initialData.items.forEach((item, index) => {
                fetchItemUnits(index, item.material_id);
            });
        }
    }, [isEdit, initialData, reset]);

    // Submit Handler
    const endpoint = isEdit
        ? `material-productions/${id}/update`
        : "material-productions/store";
    const { mutate: saveProduction, isLoading: isSubmitting } =
        usePostData(endpoint);

    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
    const [pendingData, setPendingData] = useState(null);

    const onPreSubmit = (data) => {
        const validItems = [];
        data.items.forEach((item, index) => {
            if (
                item.material_id &&
                item.warehouse_id &&
                item.quantity > 0 &&
                item.material_unit_id
            ) {
                // Resolve names for display
                const materialName = findOptionTitle(
                    allMaterials,
                    item.material_id,
                );
                const warehouseName = findOptionTitle(
                    warehouses,
                    item.warehouse_id,
                );
                const units = unitsState.items[index] || [];
                const unitName = findOptionTitle(units, item.material_unit_id);

                validItems.push({
                    ...item,
                    quantity: Number(item.quantity),
                    // Display props
                    _materialName: materialName,
                    _warehouseName: warehouseName,
                    _unitName: unitName,
                });
            }
        });

        if (validItems.length === 0) {
            showToast(
                "error",
                "Validasi Gagal",
                "Minimal satu bahan baku harus diisi lengkap.",
            );
            return;
        }

        // Resolve header names
        const outputMaterialName = findOptionTitle(
            allMaterials,
            data.material_id,
        );
        const outputWarehouseName = findOptionTitle(
            warehouses,
            data.warehouse_id,
        );
        const outputUnitName = findOptionTitle(
            unitsState.parent,
            data.material_unit_id,
        );

        const formattedData = {
            ...data,
            production_date: data.production_date.toString(),
            quantity: Number(data.quantity),
            items: validItems,
            // Display props for header
            _outputMaterialName: outputMaterialName,
            _outputWarehouseName: outputWarehouseName,
            _outputUnitName: outputUnitName,
        };

        setPendingData(formattedData);
        onOpen();
    };

    const onFinalSubmit = () => {
        if (!pendingData) return;

        // Strip display properties before sending
        const payload = {
            ...pendingData,
            items: pendingData.items.map(
                ({ _materialName, _warehouseName, _unitName, ...rest }) => rest,
            ),
        };
        // Remove header display props
        delete payload._outputMaterialName;
        delete payload._outputWarehouseName;
        delete payload._outputUnitName;

        const method = isEdit ? "put" : "post";

        saveProduction(payload, {
            method: method,
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Produksi berhasil ${isEdit ? "diperbarui" : "disimpan"}.`,
                );
                onClose();
                navigate("/productions/data");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message ||
                        err.message ||
                        "Terjadi kesalahan.",
                );
                onClose();
            },
        });
    };

    // Error state for edit mode
    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data produksi. Silakan coba lagi."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Produksi" : "Buat Produksi Baru"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Produksi", href: "/productions/data" },
                        { label: isEdit ? "Edit Produksi" : "Buat Produksi" },
                    ]}
                />
            </div>

            {isEdit && isFetching ? (
                <div className="border-default-200 bg-default-50 flex h-64 w-full items-center justify-center rounded-lg border border-dashed">
                    <Spinner
                        size="lg"
                        label="Memuat data produksi..."
                        color="primary"
                    />
                </div>
            ) : (
                <form
                    onSubmit={handleSubmit(onPreSubmit)}
                    className="flex h-full flex-col gap-4"
                >
                    {/* Main Card: Result Info */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-50 border-default-100 flex flex-col items-start border-b px-6 py-4">
                            <ThemedText size="md" className="font-semibold">
                                Informasi Produksi
                            </ThemedText>
                            <ThemedText size="xs" variant="secondary">
                                Detail produk hasil dan lokasi penyimpanan.
                            </ThemedText>
                        </CardHeader>
                        <CardBody className="grid grid-cols-1 gap-8 p-6 lg:grid-cols-2">
                            {/* Kiri: Tanggal & Gudang */}
                            <div className="flex flex-col gap-4">
                                <ThemedText className="font-semibold">
                                    Waktu & Lokasi
                                </ThemedText>
                                <Controller
                                    name="production_date"
                                    control={control}
                                    rules={{ required: "Tanggal wajib diisi" }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Tanggal Produksi"
                                            variant="bordered"
                                            {...field}
                                            errorMessage={
                                                errors.production_date?.message
                                            }
                                            isInvalid={!!errors.production_date}
                                        />
                                    )}
                                />

                                <Controller
                                    name="warehouse_id"
                                    control={control}
                                    rules={{
                                        required: "Gudang tujuan wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            label="Gudang Tujuan (Masuk)"
                                            placeholder="Pilih gudang"
                                            variant="bordered"
                                            isLoading={isLoadingWarehouses}
                                            selectedKeys={
                                                field.value
                                                    ? [String(field.value)]
                                                    : []
                                            }
                                            onChange={(e) =>
                                                field.onChange(e.target.value)
                                            }
                                            errorMessage={
                                                errors.warehouse_id?.message
                                            }
                                            isInvalid={!!errors.warehouse_id}
                                        >
                                            {(warehouses || []).map((wh) => (
                                                <SelectItem
                                                    key={wh.value}
                                                    value={wh.value}
                                                >
                                                    {wh.title}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    )}
                                />

                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <Textarea
                                            label="Catatan Produksi"
                                            placeholder="Tambahkan keterangan opsional..."
                                            variant="bordered"
                                            minRows={3}
                                            {...field}
                                        />
                                    )}
                                />
                            </div>

                            {/* Kanan: Produk Hasil */}
                            <div className="flex flex-col gap-4">
                                <ThemedText className="font-semibold">
                                    Produk Hasil
                                </ThemedText>

                                <Controller
                                    name="material_id"
                                    control={control}
                                    rules={{
                                        required: "Produk Hasil wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            label="Produk Hasil"
                                            placeholder="Pilih item"
                                            variant="bordered"
                                            isLoading={isLoadingMaterials}
                                            selectedKeys={
                                                field.value
                                                    ? [String(field.value)]
                                                    : []
                                            }
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                                // Reset unit parent
                                                setValue(
                                                    "material_unit_id",
                                                    "",
                                                );
                                            }}
                                            errorMessage={
                                                errors.material_id?.message
                                            }
                                            isInvalid={!!errors.material_id}
                                        >
                                            {(allMaterials || []).map((m) => (
                                                <SelectItem
                                                    key={m.value}
                                                    value={m.value}
                                                >
                                                    {m.title}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    )}
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <Controller
                                        name="quantity"
                                        control={control}
                                        rules={{ required: true, min: 0.01 }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Jumlah Hasil"
                                                placeholder="Hasil produksi yang diharapkan"
                                                variant="bordered"
                                                hideStepper
                                                value={field.value}
                                                onValueChange={field.onChange}
                                                errorMessage={
                                                    errors.quantity?.message
                                                }
                                                isInvalid={!!errors.quantity}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name="material_unit_id"
                                        control={control}
                                        rules={{
                                            required: "Satuan wajib dipilih",
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                label="Satuan"
                                                placeholder="Pilih satuan"
                                                variant="bordered"
                                                isDisabled={!watchMaterialId}
                                                isLoading={
                                                    unitsState.parentLoading
                                                }
                                                selectedKeys={
                                                    field.value
                                                        ? [String(field.value)]
                                                        : []
                                                }
                                                onChange={(e) =>
                                                    field.onChange(
                                                        e.target.value,
                                                    )
                                                }
                                                errorMessage={
                                                    errors.material_unit_id
                                                        ?.message
                                                }
                                                isInvalid={
                                                    !!errors.material_unit_id
                                                }
                                            >
                                                {(unitsState.parent || []).map(
                                                    (u) => (
                                                        <SelectItem
                                                            key={u.value}
                                                            value={u.value}
                                                        >
                                                            {u.title}
                                                        </SelectItem>
                                                    ),
                                                )}
                                            </Select>
                                        )}
                                    />
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Items Card: Ingredients */}
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-50 border-default-100 flex flex-row items-center justify-between border-b px-6 py-4">
                            <div className="flex flex-col">
                                <ThemedText size="md" className="font-semibold">
                                    Bahan Baku (Ingredients)
                                </ThemedText>
                                <ThemedText size="xs" variant="secondary">
                                    Daftar bahan yang digunakan dalam produksi
                                    ini.
                                </ThemedText>
                            </div>
                            <Button
                                size="sm"
                                color="primary"
                                variant="flat"
                                startContent={<LuPlus />}
                                onPress={() =>
                                    append({
                                        material_id: "",
                                        warehouse_id: watch("warehouse_id"),
                                        quantity: null,
                                        material_unit_id: "",
                                        max_stock: 0,
                                    })
                                }
                            >
                                Tambah Bahan
                            </Button>
                        </CardHeader>
                        <CardBody className="overflow-x-auto p-0">
                            <Table
                                aria-label="Tabel Bahan Baku"
                                shadow="none"
                                radius="none"
                                removeWrapper
                                classNames={{
                                    th: "bg-default-50",
                                    td: "align-top",
                                }}
                            >
                                <TableHeader>
                                    <TableColumn className="w-4/12">
                                        Bahan Baku
                                    </TableColumn>
                                    <TableColumn className="w-3/12">
                                        Gudang Asal
                                    </TableColumn>
                                    <TableColumn className="w-1/6">
                                        Jumlah
                                    </TableColumn>
                                    <TableColumn className="w-1/6">
                                        Satuan
                                    </TableColumn>
                                    <TableColumn className="w-1/12 text-center">
                                        Stok
                                    </TableColumn>
                                    <TableColumn>Aksi</TableColumn>
                                </TableHeader>
                                <TableBody>
                                    {fields.map((item, index) => (
                                        <TableRow key={item.id}>
                                            <TableCell>
                                                <Controller
                                                    name={`items.${index}.material_id`}
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <Select
                                                            aria-label="Pilih Bahan"
                                                            placeholder="Pilih Bahan"
                                                            size="sm"
                                                            variant="bordered"
                                                            isLoading={
                                                                isLoadingMaterials
                                                            }
                                                            selectedKeys={
                                                                field.value
                                                                    ? [
                                                                          String(
                                                                              field.value,
                                                                          ),
                                                                      ]
                                                                    : []
                                                            }
                                                            onChange={(e) => {
                                                                field.onChange(
                                                                    e.target
                                                                        .value,
                                                                );
                                                                // Reset unit & fetch units untuk index ini
                                                                setValue(
                                                                    `items.${index}.material_unit_id`,
                                                                    "",
                                                                );
                                                                fetchItemUnits(
                                                                    index,
                                                                    e.target
                                                                        .value,
                                                                );
                                                            }}
                                                            isInvalid={
                                                                !!errors
                                                                    .items?.[
                                                                    index
                                                                ]?.material_id
                                                            }
                                                        >
                                                            {getAvailableMaterials(
                                                                index,
                                                            ).map((m) => (
                                                                <SelectItem
                                                                    key={
                                                                        m.value
                                                                    }
                                                                    value={
                                                                        m.value
                                                                    }
                                                                >
                                                                    {m.title}
                                                                </SelectItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`items.${index}.warehouse_id`}
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <Select
                                                            aria-label="Gudang Asal"
                                                            placeholder="Pilih gudang"
                                                            size="sm"
                                                            variant="bordered"
                                                            isLoading={
                                                                isLoadingWarehouses
                                                            }
                                                            selectedKeys={
                                                                field.value
                                                                    ? [
                                                                          String(
                                                                              field.value,
                                                                          ),
                                                                      ]
                                                                    : []
                                                            }
                                                            onChange={(e) =>
                                                                field.onChange(
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            isInvalid={
                                                                !!errors
                                                                    .items?.[
                                                                    index
                                                                ]?.warehouse_id
                                                            }
                                                        >
                                                            {(
                                                                warehouses || []
                                                            ).map((w) => (
                                                                <SelectItem
                                                                    key={
                                                                        w.value
                                                                    }
                                                                    value={
                                                                        w.value
                                                                    }
                                                                >
                                                                    {w.title}
                                                                </SelectItem>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`items.${index}.quantity`}
                                                    control={control}
                                                    rules={{
                                                        required: true,
                                                        min: 0.01,
                                                        validate: (val) => {
                                                            const max =
                                                                getValues(
                                                                    `items.${index}.max_stock`,
                                                                );
                                                            if (
                                                                max !==
                                                                    undefined &&
                                                                parseFloat(
                                                                    val,
                                                                ) >
                                                                    parseFloat(
                                                                        max,
                                                                    )
                                                            ) {
                                                                return "Stok kurang";
                                                            }
                                                            return true;
                                                        },
                                                    }}
                                                    render={({ field }) => (
                                                        <NumberInput
                                                            size="sm"
                                                            variant="bordered"
                                                            hideStepper
                                                            placeholder="0"
                                                            aria-label="Qty"
                                                            value={field.value}
                                                            onValueChange={
                                                                field.onChange
                                                            }
                                                            isInvalid={
                                                                !!errors
                                                                    .items?.[
                                                                    index
                                                                ]?.quantity
                                                            }
                                                            errorMessage={
                                                                errors.items?.[
                                                                    index
                                                                ]?.quantity
                                                                    ?.message
                                                            }
                                                        />
                                                    )}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Controller
                                                    name={`items.${index}.material_unit_id`}
                                                    control={control}
                                                    rules={{ required: true }}
                                                    render={({ field }) => {
                                                        const itemMaterialId =
                                                            watch(
                                                                `items.${index}.material_id`,
                                                            );
                                                        const itemUnits =
                                                            unitsState.items[
                                                                index
                                                            ] || [];

                                                        return (
                                                            <Select
                                                                aria-label="Satuan"
                                                                placeholder="Satuan"
                                                                size="sm"
                                                                variant="bordered"
                                                                isDisabled={
                                                                    !itemMaterialId
                                                                }
                                                                isLoading={
                                                                    unitsState
                                                                        .itemsLoading[
                                                                        index
                                                                    ]
                                                                }
                                                                selectedKeys={
                                                                    field.value
                                                                        ? [
                                                                              String(
                                                                                  field.value,
                                                                              ),
                                                                          ]
                                                                        : []
                                                                }
                                                                onChange={(e) =>
                                                                    field.onChange(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                isInvalid={
                                                                    !!errors
                                                                        .items?.[
                                                                        index
                                                                    ]
                                                                        ?.material_unit_id
                                                                }
                                                            >
                                                                {itemUnits.map(
                                                                    (u) => (
                                                                        <SelectItem
                                                                            key={
                                                                                u.value
                                                                            }
                                                                            value={
                                                                                u.value
                                                                            }
                                                                        >
                                                                            {
                                                                                u.title
                                                                            }
                                                                        </SelectItem>
                                                                    ),
                                                                )}
                                                            </Select>
                                                        );
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell className="text-center">
                                                <StockInfo
                                                    control={control}
                                                    index={index}
                                                    setValue={setValue}
                                                    trigger={trigger}
                                                    units={
                                                        unitsState.items[
                                                            index
                                                        ] || []
                                                    }
                                                    onLoadingChange={
                                                        handleStockLoading
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    isIconOnly
                                                    size="sm"
                                                    color="danger"
                                                    variant="light"
                                                    onPress={() =>
                                                        remove(index)
                                                    }
                                                >
                                                    <LuTrash />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {fields.length === 0 && (
                                <div className="text-default-400 border-default-100 border-b border-dashed p-8 text-center text-sm">
                                    Belum ada bahan baku ditambahkan.
                                </div>
                            )}
                            <div className="mt-8 flex justify-end gap-3 p-4">
                                <Button
                                    variant="light"
                                    size="sm"
                                    onPress={() =>
                                        navigate("/productions/data")
                                    }
                                >
                                    Batal
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    size="sm"
                                    isDisabled={
                                        !isValid ||
                                        isSubmitting ||
                                        isStockChecking
                                    }
                                    startContent={!isSubmitting && <LuSave />}
                                >
                                    {isEdit
                                        ? "Simpan Perubahan"
                                        : "Simpan Transaksi"}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </form>
            )}
            {/* Confirmation Modal */}
            <Modal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                size="2xl"
                isDismissable={!isSubmitting}
                isKeyboardDismissDisabled={isSubmitting}
                hideCloseButton={isSubmitting}
            >
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader>
                                <ThemedText size="md">
                                    Konfirmasi Produksi
                                </ThemedText>
                            </ModalHeader>
                            <ModalBody>
                                <ThemedText size="sm">
                                    Apakah anda yakin ingin menyelesaikan
                                    produksi ini?
                                </ThemedText>
                                {pendingData && (
                                    <div className="flex flex-col gap-4">
                                        {/* Result Summary */}
                                        <div className="bg-default-50 rounded-lg p-3">
                                            <div className="grid grid-cols-2 gap-y-2">
                                                <ThemedText
                                                    size="sm"
                                                    variant="secondary"
                                                >
                                                    Tanggal
                                                </ThemedText>
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {formatDate(
                                                        pendingData.production_date,
                                                    )}
                                                </ThemedText>

                                                <ThemedText
                                                    size="sm"
                                                    variant="secondary"
                                                >
                                                    Output Produk
                                                </ThemedText>
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {
                                                        pendingData._outputMaterialName
                                                    }
                                                </ThemedText>

                                                <ThemedText
                                                    size="sm"
                                                    variant="secondary"
                                                >
                                                    Jumlah Output
                                                </ThemedText>
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {pendingData.quantity}{" "}
                                                    {
                                                        pendingData._outputUnitName
                                                    }
                                                </ThemedText>

                                                <ThemedText
                                                    size="sm"
                                                    variant="secondary"
                                                >
                                                    Gudang Tujuan
                                                </ThemedText>
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {
                                                        pendingData._outputWarehouseName
                                                    }
                                                </ThemedText>

                                                {pendingData.description && (
                                                    <>
                                                        <ThemedText
                                                            size="sm"
                                                            variant="secondary"
                                                        >
                                                            Catatan
                                                        </ThemedText>
                                                        <ThemedText
                                                            size="sm"
                                                            className="text-default-600 italic"
                                                        >
                                                            "
                                                            {
                                                                pendingData.description
                                                            }
                                                            "
                                                        </ThemedText>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Ingredients List */}
                                        <div>
                                            <ThemedText
                                                size="sm"
                                                className="mb-2 font-semibold"
                                            >
                                                Daftar Bahan Baku
                                            </ThemedText>
                                            <Table
                                                aria-label="Daftar Bahan Baku"
                                                removeWrapper
                                                shadow="none"
                                            >
                                                <TableHeader>
                                                    <TableColumn>
                                                        Barang
                                                    </TableColumn>
                                                    <TableColumn>
                                                        Gudang
                                                    </TableColumn>
                                                    <TableColumn align="end">
                                                        Qty
                                                    </TableColumn>
                                                </TableHeader>
                                                <TableBody>
                                                    {pendingData.items.map(
                                                        (item, idx) => (
                                                            <TableRow key={idx}>
                                                                <TableCell>
                                                                    <ThemedText size="sm">
                                                                        {
                                                                            item._materialName
                                                                        }
                                                                    </ThemedText>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <ThemedText size="sm">
                                                                        {
                                                                            item._warehouseName
                                                                        }
                                                                    </ThemedText>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <ThemedText size="sm">
                                                                        {
                                                                            item.quantity
                                                                        }{" "}
                                                                        {
                                                                            item._unitName
                                                                        }
                                                                    </ThemedText>
                                                                </TableCell>
                                                            </TableRow>
                                                        ),
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <Button
                                    variant="light"
                                    onPress={onClose}
                                    size="sm"
                                    isDisabled={isSubmitting}
                                >
                                    Batal
                                </Button>
                                <Button
                                    color="primary"
                                    onPress={onFinalSubmit}
                                    isLoading={isSubmitting}
                                    size="sm"
                                >
                                    Ya, Simpan Transaksi
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};

export default ProductionCreate;
